import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getStatus } from '@/api/status'
import type { StatusData } from '@/api/status'
import { useCountUp } from '@/hooks/useCountUp'

const G = '#00ff41'
const SEG = '"DSEG7 Classic", "DSEG7-Classic", monospace'

/** 7セグ風の数字（消灯セグメントをゴースト表示） */
function SevenSeg({ value, fontSize, glow = true }: { value: number; fontSize: string; glow?: boolean }) {
  const str = value.toLocaleString()
  const ghost = str.replace(/[0-9]/g, '8')
  return (
    <span style={{ position: 'relative', display: 'inline-block', fontFamily: SEG, fontSize, lineHeight: 1 }}>
      <span style={{ position: 'absolute', left: 0, top: 0, color: 'rgba(0,255,65,.13)' }} aria-hidden>
        {ghost}
      </span>
      <span
        style={{
          position: 'relative',
          color: G,
          textShadow: glow ? '0 0 12px rgba(0,255,65,.9), 0 0 28px rgba(0,255,65,.5)' : '0 0 6px rgba(0,255,65,.7)',
        }}
      >
        {str}
      </span>
    </span>
  )
}

const FIELDS: { key: keyof Omit<StatusData, 'luck'>; label: string }[] = [
  { key: 'intelligence', label: '知力' },
  { key: 'charm',        label: '魅力' },
  { key: 'mental',       label: '精神力' },
  { key: 'physical',     label: '体力' },
  { key: 'financial',    label: '財力' },
  { key: 'dexterity',    label: '器用さ' },
]

function RadarSVG({ values }: { values: number[] }) {
  const V = 300
  const cx = 150, cy = 150
  const ringR = 120, maxR = 96, markerR = 110, labelR = 134

  const maxV = Math.max(1, ...values)

  const axisXY = (i: number, r: number, ratio = 1) => {
    const θ = (i * Math.PI) / 3
    return { x: cx + r * ratio * Math.sin(θ), y: cy - r * ratio * Math.cos(θ) }
  }

  const hexPoints = (r: number, ratio = 1) =>
    values.map((v, i) => {
      const p = axisXY(i, r, ratio === 1 ? v / maxV : ratio)
      return `${p.x},${p.y}`
    }).join(' ')

  return (
    <svg viewBox={`0 0 ${V} ${V}`} style={{ display: 'block', margin: '0 auto', width: 'auto', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
      {/* 外側の装飾リング */}
      <circle cx={cx} cy={cy} r={ringR + 14} fill="none" stroke={G} strokeOpacity=".1" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={ringR + 7} fill="none" stroke={G} strokeOpacity=".2" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={ringR} fill="none" stroke={G} strokeOpacity=".7" strokeWidth="2" />

      {/* 外周のティック */}
      {Array.from({ length: 60 }, (_, i) => {
        const a = (i * 6 * Math.PI) / 180
        const long = i % 5 === 0
        const r1 = ringR + (long ? 3 : 5)
        const r2 = ringR + (long ? 11 : 8)
        return (
          <line key={i}
            x1={cx + r1 * Math.cos(a)} y1={cy + r1 * Math.sin(a)}
            x2={cx + r2 * Math.cos(a)} y2={cy + r2 * Math.sin(a)}
            stroke={G} strokeOpacity={long ? '.55' : '.22'} strokeWidth="1"
          />
        )
      })}

      {/* 六角グリッド */}
      {[0.25, 0.5, 0.75, 1].map((r) => (
        <polygon key={r} points={hexPoints(maxR, r)} fill="none"
          stroke={G} strokeOpacity={r === 1 ? '.45' : '.18'} strokeWidth="1" />
      ))}

      {/* 軸線 */}
      {values.map((_, i) => {
        const p = axisXY(i, maxR)
        return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={G} strokeOpacity=".22" strokeWidth="1" />
      })}

      {/* 塗りつぶしポリゴン */}
      <polygon points={hexPoints(maxR)} fill={G} fillOpacity=".18" stroke={G} strokeWidth="2.5" strokeOpacity=".85" />

      {/* 頂点ドット */}
      {values.map((v, i) => {
        const p = axisXY(i, maxR, v / maxV)
        return <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={G} fillOpacity=".9" />
      })}

      {/* 中央照準 */}
      <line x1={cx - 17} y1={cy} x2={cx - 6} y2={cy} stroke={G} strokeOpacity=".9" strokeWidth="1.5" />
      <line x1={cx + 6} y1={cy} x2={cx + 17} y2={cy} stroke={G} strokeOpacity=".9" strokeWidth="1.5" />
      <line x1={cx} y1={cy - 17} x2={cx} y2={cy - 6} stroke={G} strokeOpacity=".9" strokeWidth="1.5" />
      <line x1={cx} y1={cy + 6} x2={cx} y2={cy + 17} stroke={G} strokeOpacity=".9" strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r="4.5" fill="none" stroke={G} strokeWidth="1.5" strokeOpacity=".9" />

      {/* ▼ マーカー（軸方向に回転、中心に向く） */}
      {values.map((_, i) => {
        const p = axisXY(i, markerR)
        return (
          <g key={i} transform={`translate(${p.x},${p.y}) rotate(${i * 60})`}>
            {/* 下向き三角形 = ▼ */}
            <polygon points="0,5 -5,-3 5,-3" fill={G} fillOpacity=".9" />
          </g>
        )
      })}

      {/* ステータスラベル＋数値 */}
      {FIELDS.map(({ label }, i) => {
        const p = axisXY(i, labelR)
        return (
          <g key={i}>
            <text x={p.x} y={p.y - 6}
              textAnchor="middle" dominantBaseline="middle"
              fill={G} fontSize="10" fontFamily="monospace" fillOpacity=".85"
            >
              {label}
            </text>
            <text x={p.x} y={p.y + 9}
              textAnchor="middle" dominantBaseline="middle"
              fill={G} fontSize="12" fontFamily={SEG}
              style={{ filter: 'drop-shadow(0 0 3px rgba(0,255,65,.7))' }}
            >
              {values[i].toLocaleString()}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export default function StatusPage() {
  const { data, isLoading } = useQuery({ queryKey: ['status'], queryFn: getStatus })
  const [powering, setPowering] = useState(true)

  // データ表示開始時に電源オン演出を再生
  useEffect(() => {
    if (isLoading) return
    // ロード完了を起点に電源オン演出を再生するための意図的な同期更新
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPowering(true)
    const t = setTimeout(() => setPowering(false), 1100)
    return () => clearTimeout(t)
  }, [isLoading])

  const values = FIELDS.map(({ key }) => data?.[key] ?? 0)
  const total = values.reduce((s, v) => s + v, 0)
  const luck = data?.luck ?? 0

  const level = data?.level ?? 0
  const earned = data?.total_earned_points ?? 0
  const levelFloor = data?.current_level_points ?? 0
  const levelCeil = data?.next_level_points ?? 0
  // 次レベルまでの進捗。分母が 0 以下になる異常時は 0 扱いにする。
  const levelSpan = levelCeil - levelFloor
  const intoLevel = Math.max(0, earned - levelFloor)
  const levelRatio = levelSpan > 0 ? Math.min(1, intoLevel / levelSpan) : 0
  const toNextLevel = Math.max(0, levelCeil - earned)

  const enabled = !isLoading && !powering
  const countedTotal = useCountUp(total, { enabled })
  const countedLuck  = useCountUp(luck,  { enabled, duration: 900 })
  const countedLevel = useCountUp(level, { enabled, duration: 900 })
  const countedEarned = useCountUp(earned, { enabled })

  if (isLoading) {
    return (
      <div className="-mx-4 flex items-center justify-center" style={{ background: '#000d00', height: 'calc(100svh - 110px - env(safe-area-inset-top))' }}>
        <p className="font-mono text-sm tracking-[.3em] animate-pulse" style={{ color: G }}>
          SCANNING...
        </p>
      </div>
    )
  }

  return (
    <div
      className="-mx-4 relative flex flex-col overflow-hidden"
      style={{ background: '#000d00', height: 'calc(100svh - 110px - env(safe-area-inset-top))' }}
    >
      {/* スキャンライン */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,.12) 2px,rgba(0,0,0,.12) 3px)',
          zIndex: 0,
        }}
      />

      {/* 電源オン時のノイズ（砂嵐） */}
      {powering && (
        <div
          className="scouter-noise-overlay absolute inset-0 pointer-events-none"
          style={{ zIndex: 2, mixBlendMode: 'screen' }}
        />
      )}

      <div
        className={`relative z-[1] flex flex-1 min-h-0 flex-col px-4 pt-2 ${powering ? 'scouter-power-on' : 'scouter-idle-flicker'}`}
        style={{ paddingBottom: 60 }}
      >
        {/* ヘッダー */}
        <div className="flex justify-between font-mono text-[9px] tracking-[.3em] shrink-0"
          style={{ color: 'rgba(0,255,65,.45)' }}>
          <span>◈ SCOUTER SYS</span>
          <span>TARGET LOCK</span>
          <span>v2.0 ◈</span>
        </div>

        {/* レーダー（残り高さいっぱい） */}
        <div className="flex-1 min-h-0 flex items-center justify-center py-1">
          <RadarSVG values={values} />
        </div>

        {/* レベル＋次レベルまでの進捗 */}
        <div
          className="rounded-sm border px-3 py-2 shrink-0"
          style={{ borderColor: 'rgba(0,255,65,.25)', background: 'rgba(0,255,65,.03)' }}
        >
          {/* 累計獲得ポイント（減衰しないレベルの元数値） */}
          <div
            className="mb-1.5 flex items-baseline justify-between border-b pb-1.5 font-mono"
            style={{ borderColor: 'rgba(0,255,65,.15)' }}
          >
            <span className="text-[10px] tracking-[.35em]" style={{ color: 'rgba(0,255,65,.5)' }}>
              TOTAL PT
            </span>
            <SevenSeg value={countedEarned} fontSize="15px" glow={false} />
          </div>

          <div className="flex items-baseline justify-between font-mono">
            <span className="text-[10px] tracking-[.35em]" style={{ color: 'rgba(0,255,65,.5)' }}>
              LV
            </span>
            <SevenSeg value={countedLevel} fontSize="20px" />
            <span className="text-[10px] tracking-wider" style={{ color: 'rgba(0,255,65,.5)' }}>
              次のレベルまで {toNextLevel.toLocaleString()}pt
            </span>
          </div>
          <div
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: 'rgba(0,255,65,.10)' }}
          >
            <div
              className="h-full rounded-full transition-[width] duration-1000 ease-out"
              style={{
                width: `${(enabled ? levelRatio : 0) * 100}%`,
                background: `linear-gradient(90deg, rgba(0,255,65,.55), ${G})`,
                boxShadow: `0 0 8px rgba(0,255,65,.7)`,
              }}
            />
          </div>
        </div>

        {/* 戦闘力 */}
        <div className="text-center shrink-0 mt-2">
          <p className="font-mono text-[10px] tracking-[.45em] mb-0.5" style={{ color: 'rgba(0,255,65,.5)' }}>
            BATTLE FORCE
          </p>
          <SevenSeg value={countedTotal} fontSize="clamp(36px,11vw,52px)" />
          <p className="font-mono text-xs tracking-[.5em] mt-1" style={{ color: 'rgba(0,255,65,.65)' }}>
            戦 闘 力
          </p>
        </div>

        {/* 運（LUCK のみ独立表示） */}
        <div
          className="mt-2 rounded-sm border px-3 py-2 flex justify-between items-center font-mono text-[11px] shrink-0"
          style={{ borderColor: 'rgba(0,255,65,.25)', background: 'rgba(0,255,65,.03)', color: 'rgba(0,255,65,.72)' }}
        >
          <span className="tracking-wider">運（LUCK）</span>
          <SevenSeg value={countedLuck} fontSize="15px" glow={false} />
        </div>
      </div>
    </div>
  )
}
